import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useSearchParams } from 'react-router-dom';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Eye, MousePointerClick, LogIn } from 'lucide-react';
import { useTheme } from '../context/ThemeContext';
import { organizationService } from '../api';
import { ThemedText } from './';
import { Card } from './Card';
import { FormLabel } from './FormStyles';

const Toolbar = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: ${props => props.theme.spacing.md};
  flex-wrap: wrap;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SelectorGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
`;

const DemoSelect = styled.select`
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  cursor: pointer;
  min-width: 240px;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const RangeBar = styled.div`
  display: inline-flex;
  gap: 4px;
  padding: 4px;
  background: ${props => props.theme.colors.backgroundSecondary};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
`;

const RangeButton = styled.button`
  padding: 6px 12px;
  background: ${({ $active, theme }) => ($active ? theme.colors.background : 'transparent')};
  border: none;
  border-radius: ${props => props.theme.borderRadius.sm};
  color: ${({ $active, theme }) => ($active ? theme.colors.text : theme.colors.muted)};
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 500)};
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const StatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: ${props => props.theme.spacing.md};
  margin-bottom: ${props => props.theme.spacing.lg};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const StatRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
`;

const StatIconWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 46px;
  height: 46px;
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => (props.$accent || props.theme.colors.tint)}1a;
  color: ${props => props.$accent || props.theme.colors.tint};
  flex-shrink: 0;
`;

const StatValue = styled.div`
  font-size: 1.75rem;
  font-weight: 700;
  color: ${props => props.theme.colors.text};
  line-height: 1.1;
  margin-top: 2px;
`;

const ChartContainer = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.border};
  box-shadow: ${props => props.theme?.shadows?.sm};
`;

const CenterState = styled.div`
  text-align: center;
  padding: ${props => props.theme.spacing.xl};
  color: ${props => props.theme.colors.muted};
`;

const RANGES = [7, 30, 90];

const fmtDate = (iso) => {
  const dt = new Date(iso);
  return Number.isNaN(dt.getTime())
    ? iso
    : dt.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/**
 * Statistics tab for Demo Studio. Reads `?org=` to know which demo is selected and
 * renders totals + a daily Views/Launches/Logins time series for the chosen window.
 * @param {{ demos: Array }} props - Demo organizations (passed from DemoStudioPage to avoid a double fetch).
 */
export const DemoStatsSection = ({ demos = [] }) => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);

  const requestedId = searchParams.get('org');
  const selectedOrg = demos.find(d => String(d.id) === String(requestedId)) || demos[0];
  const orgId = selectedOrg?.id;

  useEffect(() => {
    if (!orgId) {
      setStats(null);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await organizationService.getDemoStats(orgId, days);
        if (!cancelled) setStats(data);
      } catch (error) {
        console.error('Failed to load demo stats:', error);
        if (!cancelled) setStats(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [orgId, days]);

  const selectOrg = (value) => {
    setSearchParams(prev => {
      prev.set('tab', 'stats');
      prev.set('org', String(value));
      return prev;
    });
  };

  if (demos.length === 0) {
    return (
      <CenterState theme={theme}>
        <ThemedText type="defaultSemiBold">No demos yet</ThemedText>
        <ThemedText type="small" style={{ color: theme.colors.muted, display: 'block', marginTop: theme.spacing.xs }}>
          Create a demo organization to start tracking views, launches, and logins.
        </ThemedText>
      </CenterState>
    );
  }

  const totalViews = loading ? '—' : (stats?.totalViews ?? 0);
  const totalLaunches = loading ? '—' : (stats?.totalLaunches ?? 0);
  const totalLogins = loading ? '—' : (stats?.totalLogins ?? 0);

  const hasActivity = !!stats && ((stats.totalViews || 0) + (stats.totalLaunches || 0) + (stats.totalLogins || 0)) > 0;
  const chartData = (stats?.daily || []).map(d => ({
    name: fmtDate(d.date),
    views: d.views,
    launches: d.launches,
    logins: d.logins,
  }));

  const cards = [
    { label: 'Total views', value: totalViews, icon: <Eye size={20} />, accent: theme.colors.tint },
    { label: 'Total launch clicks', value: totalLaunches, icon: <MousePointerClick size={20} />, accent: theme.colors.primary },
    { label: 'Total viewer logins', value: totalLogins, icon: <LogIn size={20} />, accent: theme.colors.success },
  ];

  return (
    <div>
      <Toolbar theme={theme}>
        <SelectorGroup theme={theme}>
          <FormLabel theme={theme}>Demo</FormLabel>
          <DemoSelect theme={theme} value={orgId ?? ''} onChange={(e) => selectOrg(e.target.value)}>
            {demos.map(org => (
              <option key={org.id} value={org.id}>{org.name}</option>
            ))}
          </DemoSelect>
        </SelectorGroup>
        <RangeBar theme={theme}>
          {RANGES.map(r => (
            <RangeButton key={r} theme={theme} $active={days === r} onClick={() => setDays(r)}>
              {r} days
            </RangeButton>
          ))}
        </RangeBar>
      </Toolbar>

      <StatGrid theme={theme}>
        {cards.map(card => (
          <Card key={card.label} theme={theme}>
            <StatRow theme={theme}>
              <StatIconWrap theme={theme} $accent={card.accent}>{card.icon}</StatIconWrap>
              <div>
                <ThemedText type="small" style={{ color: theme.colors.muted }}>{card.label}</ThemedText>
                <StatValue theme={theme}>{card.value}</StatValue>
              </div>
            </StatRow>
          </Card>
        ))}
      </StatGrid>

      <ChartContainer theme={theme}>
        <ThemedText type="subtitle" style={{ display: 'block', marginBottom: theme.spacing.lg }}>
          Daily activity — last {days} days
        </ThemedText>
        {loading ? (
          <CenterState theme={theme}><ThemedText>Loading analytics...</ThemedText></CenterState>
        ) : !hasActivity ? (
          <CenterState theme={theme}>
            <ThemedText type="defaultSemiBold">No demo activity yet</ThemedText>
            <ThemedText type="small" style={{ color: theme.colors.muted, display: 'block', marginTop: theme.spacing.xs }}>
              Views, launches, and logins will appear here once visitors start exploring this demo.
            </ThemedText>
          </CenterState>
        ) : (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" stroke={theme.colors.border} />
              <XAxis dataKey="name" stroke={theme.colors.muted} style={{ fontSize: '12px' }} />
              <YAxis stroke={theme.colors.muted} allowDecimals={false} style={{ fontSize: '12px' }} />
              <Tooltip
                contentStyle={{
                  backgroundColor: theme.colors.backgroundSecondary,
                  border: `1px solid ${theme.colors.border}`,
                  borderRadius: theme.borderRadius.md,
                  color: theme.colors.text,
                }}
                labelStyle={{ color: theme.colors.text }}
              />
              <Legend wrapperStyle={{ fontSize: '12px', color: theme.colors.text }} />
              <Line type="monotone" dataKey="views" stroke={theme.colors.tint} strokeWidth={2} dot={false} activeDot={{ r: 5 }} name="Views" />
              <Line type="monotone" dataKey="launches" stroke={theme.colors.primary} strokeWidth={2} dot={false} activeDot={{ r: 5 }} name="Launches" />
              <Line type="monotone" dataKey="logins" stroke={theme.colors.success} strokeWidth={2} dot={false} activeDot={{ r: 5 }} name="Logins" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartContainer>
    </div>
  );
};

export default DemoStatsSection;
